"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { ClickableTableRow } from "@/components/ClickableTableRow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trophy, Medal, Award, LogIn, ExternalLink } from "lucide-react";
import { type RangeKey, type ReportRowClient } from "@/types/topWebsites";
import config from "@/config";
import { extractHostname, formatDisplayUrl } from "@/lib/url";

interface TopWebsitesClientProps {
  resultsByRange: Record<RangeKey, ReportRowClient[]>;
  isAuthenticated: boolean;
  currentUserId?: string | null;
  showHeader?: boolean;
  colorScheme?: "orange" | "green";
}

export function TopWebsitesClient({
  resultsByRange,
  isAuthenticated,
  currentUserId = null,
  showHeader = true,
  colorScheme = "orange",
}: TopWebsitesClientProps) {
  const [range, setRange] = React.useState<RangeKey>("all");

  const reports = resultsByRange[range] ?? [];
  const showAuthNotice = !isAuthenticated;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4">
      {showHeader && (
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="inline-flex items-center px-3 py-1 rounded-full border border-[#f97316] mb-4">
            <span className="text-xs font-medium text-[#f97316] dark:text-[#f97316] uppercase tracking-wider">
              Leaderboard
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-center mb-4">
            See who&apos;s leading the
            <span className="block text-[#f97316] pb-1">website race</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Discover the best performing websites and see how you compare
          </p>
          <a
            href="/signin"
            className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-white bg-[#f97316] hover:bg-[#ea580c] rounded-lg transition-colors"
          >
            Start Free Analysis
          </a>
        </div>
      )}

      <nav className="mt-2 flex items-center justify-center gap-1 text-xs">
        {[
          { key: "last24h" as RangeKey, label: "Last 24h" },
          { key: "last30d" as RangeKey, label: "Last 30d" },
          { key: "all" as RangeKey, label: "All time" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setRange(opt.key)}
            className={`px-2 py-1 rounded-md border font-medium transition-colors cursor-pointer ${
              range === opt.key
                ? `bg-background text-foreground ${
                    colorScheme === "green"
                      ? "border-emerald-500"
                      : "border-[#f97316]"
                  }`
                : "text-muted-foreground hover:text-foreground hover:bg-muted/40 hover:border-input/60 border-transparent"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </nav>

      {showAuthNotice && (
        <div className="mt-3 flex justify-center">
          <div className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border/60 bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground sm:gap-2">
            <LogIn className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
            <span className="text-center leading-tight sm:text-left">
              Log in to join the leaderboard and track your website&apos;s
              performance.{" "}
              <Link
                href={config.routes.signin.path}
                className="font-medium text-primary underline underline-offset-4"
              >
                Sign in for free
              </Link>
              .
            </span>
          </div>
        </div>
      )}

      <div className="w-full overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[6%] text-sm">#</TableHead>
              <TableHead className="w-[40%] text-sm">Site</TableHead>
              <TableHead className="w-[18%] text-sm">Overall</TableHead>
              <TableHead className="w-[24%] text-sm">Scores</TableHead>
              <TableHead className="w-[14%] text-sm">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="h-24 text-center text-muted-foreground"
                >
                  No data for this period.
                </TableCell>
              </TableRow>
            ) : (
              reports.map((r, idx) => {
                const isOwnReport = Boolean(
                  currentUserId && r.userId === currentUserId
                );
                const rawDisplay = r.domain ?? r.url;
                const { hostname, href } = extractHostname(rawDisplay);
                const displayUrl = formatDisplayUrl(rawDisplay);
                const faviconHost = hostname || rawDisplay;

                return (
                  <ClickableTableRow
                    key={r.id}
                    id={r.id}
                    className={`h-16 transition-all duration-300 ${
                      isOwnReport
                        ? colorScheme === "green"
                          ? "bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent hover:from-emerald-500/15 hover:via-emerald-500/8 shadow-[inset_0_0_20px_rgba(16,185,129,0.1)]"
                          : "bg-gradient-to-r from-[#f97316]/10 via-[#f97316]/5 to-transparent hover:from-[#f97316]/15 hover:via-[#f97316]/8 shadow-[inset_0_0_20px_rgba(249,115,22,0.1)]"
                        : ""
                    }`}
                  >
                    <TableCell
                      className={`text-muted-foreground font-medium ${
                        isOwnReport
                          ? colorScheme === "green"
                            ? "border-l-[3px] border-l-emerald-500"
                            : "border-l-[3px] border-l-[#f97316]"
                          : ""
                      }`}
                    >
                      {renderRankCell(idx)}
                    </TableCell>
                    <TableCell className="max-w-0">
                      <div className="flex items-center gap-4 min-w-0">
                        {faviconHost && (
                          <Image
                            src={`https://www.google.com/s2/favicons?domain=${faviconHost}&sz=48`}
                            alt="Favicon"
                            width={24}
                            height={24}
                            unoptimized
                            className="rounded-md shrink-0"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                        <div className="min-w-0 space-y-1">
                          {href ? (
                            <Link
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="group inline-flex max-w-full items-center gap-1.5 font-semibold text-foreground transition-colors hover:text-primary"
                            >
                              <span className="truncate transition-colors group-hover:underline group-hover:underline-offset-4">
                                {displayUrl}
                              </span>
                              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                            </Link>
                          ) : (
                            <span className="font-semibold text-foreground truncate">
                              {displayUrl}
                            </span>
                          )}
                          <div className="flex items-center gap-2 text-sm text-muted-foreground truncate">
                            <span className="truncate">{r.url}</span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{renderOverallInline(r.overallScore)}</TableCell>
                    <TableCell>
                      <div className="flex items-start gap-3 sm:gap-4">
                        {renderScoreStack("Speed", r.psiOverallScore)}
                        {renderScoreStack(
                          "Quality",
                          r.siteAnalysisOverallScore
                        )}
                        {renderScoreStack(
                          "Security",
                          r.trustSecurityOverallScore
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground sm:text-sm">
                      {r.createdAtRelative}
                    </TableCell>
                  </ClickableTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function renderScoreStack(label: string, score?: number | null) {
  const value = typeof score === "number" ? Math.round(score) : null;
  const dotColor = getScoreDotClass(value);
  return (
    <div className="min-w-[56px]">
      <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground/70 leading-none">
        <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
        <span>{label}</span>
      </div>
      <div className="font-semibold text-foreground/90 text-sm sm:text-base leading-tight">
        {value ?? "-"}
      </div>
    </div>
  );
}

function getScoreDotClass(value: number | null) {
  if (value === null) return "bg-muted-foreground/30";
  if (value >= 80) return "bg-emerald-500";
  if (value >= 60) return "bg-orange-500";
  return "bg-rose-500";
}

function renderOverallInline(score?: number | null) {
  const value = typeof score === "number" ? Math.round(score) : null;
  if (value === null) {
    return (
      <span className="inline-flex items-baseline text-xs text-muted-foreground/80">
        -
      </span>
    );
  }
  const color = getScoreColorHex(value);
  return (
    <div className="inline-flex items-baseline gap-1">
      <span
        className="font-black leading-none text-base sm:text-lg"
        style={{ color }}
      >
        {value}
      </span>
      <span className="text-[10px] sm:text-xs leading-none">/100</span>
    </div>
  );
}

function getScoreColorHex(value: number) {
  if (value >= 80) return "#10b981"; // green-500
  if (value >= 60) return "#f97316"; // orange-500
  return "#ef4444"; // red-500
}

function renderRankCell(index: number) {
  const rank = index + 1;
  if (rank === 1) {
    return (
      <span className="inline-flex items-center justify-center w-6">
        <Trophy className="h-5 w-5 text-yellow-400" aria-label="1st place" />
      </span>
    );
  }
  if (rank === 2) {
    return (
      <span className="inline-flex items-center justify-center w-6">
        <Medal className="h-5 w-5 text-slate-300" aria-label="2nd place" />
      </span>
    );
  }
  if (rank === 3) {
    return (
      <span className="inline-flex items-center justify-center w-6">
        <Award className="h-5 w-5 text-amber-500" aria-label="3rd place" />
      </span>
    );
  }
  return <span>{rank}</span>;
}
